import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Loader2 } from "lucide-react";
import { AuthShell } from "./Signup";
import { supabase } from "../lib/supabase";
import PasswordInput from "../components/PasswordInput";

// A valid post-login destination must be an in-app path, never an absolute
// URL (prevents open-redirect attacks).
function safeNext(raw) {
  if (!raw) return null;
  try {
    const decoded = decodeURIComponent(raw);
    if (decoded.startsWith("/") && !decoded.startsWith("//")) return decoded;
  } catch { /* ignore */ }
  return null;
}

export default function Login() {
  const { signIn, setProfile } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nextPath = safeNext(searchParams.get("next"));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    const { data, error: signInError } = await signIn({ email, password });
    if (signInError) {
      setError(signInError.message);
      setBusy(false);
      return;
    }
    const userId = data?.user?.id;
    if (!userId) {
      setError("Login failed. Please try again.");
      setBusy(false);
      return;
    }
    const { data: profile } = await supabase
      .from("users")
      .select("*")
      .eq("id", userId)
      .maybeSingle();

    if (profile) setProfile(profile);

    if (profile?.onboarding_complete) {
      // Honor the deep-link they came from (shared article/recipe/etc).
      navigate(nextPath || "/app", { replace: true });
    } else {
      // Preserve the deep link through onboarding so they still land on the
      // shared content after completing setup.
      navigate(
        nextPath ? `/onboarding?next=${encodeURIComponent(nextPath)}` : "/onboarding",
        { replace: true },
      );
    }
  };

  return (
    <AuthShell title="Welcome back" subtitle="Continue your glow ritual.">
      <form onSubmit={submit} className="space-y-3" data-testid="login-form">
        <input
          type="email"
          required
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid="login-email"
          className="w-full rounded-full bg-white border border-cocoa/10 px-5 py-3.5 text-sm placeholder:text-cocoa/40 focus:outline-none focus:ring-2 focus:ring-rose/40"
        />
        <PasswordInput
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Password"
          autoComplete="current-password"
          testId="login-password"
          className="rounded-full bg-white border border-cocoa/10 px-5 py-3.5 text-sm placeholder:text-cocoa/40 focus:outline-none focus:ring-2 focus:ring-rose/40"
        />
        {error && <p className="text-sm text-red-500 font-light px-2" data-testid="login-error">{error}</p>}
        <button
          type="submit"
          disabled={busy}
          data-testid="login-submit"
          className="w-full rounded-full bg-rose hover:bg-rose-dark text-white py-3.5 font-medium transition-all shadow-rose flex items-center justify-center gap-2"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Log in"}
        </button>
      </form>
      <Link
        to="/forgot-password"
        data-testid="login-forgot-link"
        className="block text-center text-sm text-cocoa/60 mt-3 hover:text-rose"
      >
        Forgot password?
      </Link>
      <p className="text-center text-sm text-cocoa/65 font-light mt-6">
        New here?{" "}
        <Link to={nextPath ? `/signup?next=${encodeURIComponent(nextPath)}` : "/signup"} data-testid="login-to-signup" className="text-rose font-medium">
          Create an account
        </Link>
      </p>
    </AuthShell>
  );
}
